import { Share07 } from "@untitledui/icons";
import { cx, notify } from "@mfa-aff/ui";
import { Dropdown } from "@mfa-aff/ui/components/base/dropdown/dropdown";
import { Button } from "@mfa-aff/ui/components/base/buttons/button";
import { useClipboard } from "@mfa-aff/ui/hooks/use-clipboard";
import { useRecordShare } from "../api/me";
import { RUBIKA_WEB, SHARE_TARGETS, type SharePayload, type ShareTarget, canNativeShare, fullText, shareHref } from "./share-links";

export type ShareMenuProps = SharePayload & {
    /** Visible label; omit for an icon-only button (then `ariaLabel` names it). */
    label?: string;
    ariaLabel?: string;
    size?: "xs" | "sm" | "md" | "lg";
    color?: "primary" | "secondary" | "tertiary";
    className?: string;
    /** Called after any share (besides recording it for the getting-started checklist). */
    onShared?: () => void;
};

const Dot = ({ color }: { color: string }) => <span aria-hidden="true" className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />;

/**
 * Compact «ارسال» menu next to every copy button: Telegram, WhatsApp, Bale, Eitaa, Rubika and, where
 * the device has one, the system share sheet. Each press ticks «اولین اشتراک‌گذاری» on the server.
 */
export const ShareMenu = ({ url, text, label, ariaLabel, size = "sm", color = "secondary", className, onShared }: ShareMenuProps) => {
    const record = useRecordShare();
    const clipboard = useClipboard();
    const payload: SharePayload = { url, text };
    const visible = label ?? (ariaLabel ? undefined : "ارسال");

    const shareTo = async (key: ShareTarget | "native") => {
        if (key === "native") {
            try {
                await navigator.share(url ? { text, url } : { text });
            } catch {
                return; // dismissed
            }
        } else {
            const href = shareHref(key, payload);
            if (href) {
                window.open(href, "_blank", "noopener,noreferrer");
            } else {
                const r = await clipboard.copy(fullText(payload));
                if (!r.success) {
                    notify.error("کپی نشد", { description: "متن را انتخاب کنید و دستی کپی کنید." });
                    return;
                }
                notify.success("متن کپی شد", { description: "روبیکا باز می‌شود؛ متن را در گفت‌وگو یا کانال مورد نظر paste کنید.", duration: 4000 });
                window.open(RUBIKA_WEB, "_blank", "noopener,noreferrer");
            }
        }
        record(key);
        onShared?.();
    };

    return (
        <Dropdown.Root>
            <Button size={size} color={color} iconLeading={Share07} aria-label={ariaLabel ?? visible} className={cx("shrink-0", className)}>
                {visible}
            </Button>
            <Dropdown.Popover className="w-52">
                <Dropdown.Menu aria-label="ارسال به" onAction={(key) => void shareTo(key as ShareTarget | "native")}>
                    {SHARE_TARGETS.map((t) => (
                        <Dropdown.Item key={t.key} id={t.key} textValue={t.label}>
                            <span className="flex items-center gap-2.5">
                                <Dot color={t.color} />
                                {t.label}
                            </span>
                        </Dropdown.Item>
                    ))}
                    {canNativeShare() ? (
                        <Dropdown.Item id="native" textValue="برنامه‌های دیگر" icon={Share07}>
                            برنامه‌های دیگر
                        </Dropdown.Item>
                    ) : null}
                </Dropdown.Menu>
            </Dropdown.Popover>
        </Dropdown.Root>
    );
};
